import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OpenAPIV3_1 as OpenApi } from 'openapi-types';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  Select,
  Tabs,
  Typography,
  useClipboard,
} from '@robocorp/components';
import {
  IconCheck2,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconFileText,
  IconLightBulb,
  IconPlay,
  IconShare,
} from '@robocorp/icons/iconic';
import { DocsLinks } from '~/constants';
import {
  ExternalApi,
  ExternalApiEndpoint,
  useCurrentWorkspace,
  useExternalApiQuery,
  useRequestMutation,
} from '~/queries';
import { withErrorBoundary } from '~/hocs';
import { ApiHelperContext, ApiHelperContextType } from './context';
import {
  ApiHelperInput,
  ApiHelperParameter,
  SelectApiKey,
  SelectArtifactId,
  SelectAssetId,
  SelectAssetUploadId,
  SelectAssistantId,
  SelectEnvironment,
  SelectEnvironmentGroup,
  SelectEnvironmentGroupLinkToken,
  SelectProcessId,
  SelectProcessRun,
  SelectRobotId,
  SelectRobotRunId,
  SelectRobotTask,
  SelectWorkItem,
  SelectWorkItemFile,
} from './components';
import CodeEditor from '../CodeEditor';
import { OldApiHelper } from './old';
import { SelectWebhookId } from './components/SelectWebhookId';
import { SelectAssistantRunId } from './components/SelectAssistantRunId';
import { getPayloadInputs } from './utils/payload';
import {
  getEndpointPath,
  getIsValidEndpointPath,
  getIsValidPayload,
  getRequestOutputs,
} from './utils/request';
import {
  getApiServer,
  getEndpointBadgeOption,
  getEndpointLink,
  getEndpointParameters,
  getEndpointsOptions,
  getIsIntegrationEndpoint,
} from './utils/helper';
import ErrorBoundary from '../ErrorBoundary';

const parameterMapper = {
  asset_id: SelectAssetId,
  upload_id: SelectAssetUploadId,
  process_id: SelectProcessId,
  process_run_id: SelectProcessRun,
  step_run_id: SelectRobotRunId,
  artifact_id: SelectArtifactId,
  work_item_id: SelectWorkItem,
  file_id: SelectWorkItemFile,
  task_package_id: SelectRobotId,
  task: SelectRobotTask,
  assistant_id: SelectAssistantId,
  webhook_id: SelectWebhookId,
  worker_id: SelectEnvironment,
  worker_group_id: SelectEnvironmentGroup,
  link_token_id: SelectEnvironmentGroupLinkToken,
  assistant_run_id: SelectAssistantRunId,
};

/**
 * Prebuild path parameters from url when possible
 */
const usePathParameters = () => {
  const { pathname } = useLocation();

  const { data: currentWorkspace } = useCurrentWorkspace<'safeToParse'>();
  const currentWorkspaceId = currentWorkspace.id;

  const [pathParameters, setPathParameters] = useState<ApiHelperContextType['pathParameters']>({
    workspace_id: currentWorkspaceId,
  });

  /**
   * Presets default API endpoint variables based on the current active route
   *
   */
  useEffect(() => {
    const processId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/*',
      pathname,
    )?.params?.processId;
    const processRunId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/runs/:processRunId/*',
      pathname,
    )?.params?.processRunId;
    const stepRunRunId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/runs/:pRunId/stepRuns/:stepRunId/*',
      pathname,
    )?.params?.stepRunId;
    const assistantId = matchPath(
      '/:organizationShortName/:workspaceShortName/assistants/:assistantId/*',
      pathname,
    )?.params?.assistantId;
    const assistantRunId = matchPath(
      '/:organizationShortName/:workspaceShortName/assistants/:assistantId/runs/:assistantRunId/*',
      pathname,
    )?.params?.assistantRunId;
    const taskPackageId = matchPath(
      '/:organizationShortName/:workspaceShortName/robots/:robotId/*',
      pathname,
    )?.params?.robotId;
    const environmentId = matchPath(
      '/:organizationShortName/:workspaceShortName/workers/:environmentId/*',
      pathname,
    )?.params?.environmentId;
    const environmentGroupId = matchPath(
      '/:organizationShortName/:workspaceShortName/workers/groups/:environmentGroupId/*',
      pathname,
    )?.params?.environmentGroupId;
    const webhookId = matchPath(
      '/:organizationShortName/:workspaceShortName/api/webhooks/:webhookId/*',
      pathname,
    )?.params?.webhookId;

    setPathParameters((previous) => ({
      ...previous,
      workspace_id: currentWorkspaceId,
      assistant_id: assistantId,
      task_package_id: taskPackageId,
      process_id: processId,
      process_run_id: processRunId,
      step_run_id: stepRunRunId,
      worker_id: environmentId,
      worker_group_id: environmentGroupId,
      webhook_id: webhookId,
      assistant_run_id: assistantRunId,
    }));
  }, [currentWorkspaceId, pathname]);

  return [pathParameters, setPathParameters] as const;
};

const ExamplePayload: FC = () => {
  const { t } = useTranslation();
  const { payload, setPayload, payloads } = useContext(ApiHelperContext);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [localPayload, setLocalPayload] = useState<string | null>(null);

  const onTabChange = (activeTabIndex: number, payloadVariant: string) => {
    setActiveTab(activeTabIndex);
    setPayload(payloadVariant);
    setLocalPayload(null);
  };

  const onPayloadChange = useCallback(
    (text: string) => {
      if (getIsValidPayload(text) || (payloads.required === false && text === '')) {
        setPayload(text);
        setLocalPayload(null);
      } else {
        setLocalPayload(text);
      }
    },
    [payloads.required, setPayload],
  );

  const activePayload = localPayload ?? payload;
  const activePayloadTab = activePayload === undefined ? -1 : activeTab;

  const defaultPayload = payloads.list[activeTab]?.payload ?? '';
  const hasError = payloads.required
    ? localPayload !== null
    : localPayload !== null && localPayload !== '';

  return payloads.list.length > 0 ? (
    <Box mb="$48">
      <Tabs activeTab={activePayloadTab} setActiveTab={setActiveTab}>
        {payloads.list.map(({ name, payload: payloadVariant }, index) => (
          <Tabs.Tab onClick={() => onTabChange(index, payloadVariant)} key={name}>
            {name}
          </Tabs.Tab>
        ))}
      </Tabs>

      {payload !== undefined ? (
        <CodeEditor
          key={defaultPayload}
          mode="json"
          value={activePayload ?? defaultPayload}
          onChange={onPayloadChange}
          error={hasError ? t('jsonEditor.invalidJSONError') : undefined}
          lineNumbers={false}
          maxHeight={200}
          lineWrapping
        />
      ) : (
        <Box borderRadius="$12" p="$8" backgroundColor="background.subtle">
          <Box display="flex" alignItems="center" gap="$8" p={2}>
            <IconLightBulb color="content.accent" />
            <Box flex="1" py={2}>
              <Typography variant="body.medium" fontWeight="medium">
                {t('noPayload')}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  ) : null;
};

interface ToolbarProps {
  path: string;
  endpoint: ExternalApiEndpoint;
  apiKey: string;
  content?: string;
  onResponseCallback: (response: string | null) => void;
  data?: string;
  isValid?: boolean;
  children?: ReactNode;
}

const Toolbar: FC<ToolbarProps> = ({
  path,
  endpoint,
  content,
  isValid,
  onResponseCallback,
  children,
  ...rest
}) => {
  const { t } = useTranslation();
  const { onCopyToClipboard, copiedToClipboard } = useClipboard();
  const { mutateAsync: apiRequestAsync, isLoading } = useRequestMutation();

  const onRequest = () => {
    return apiRequestAsync({
      url: path,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      method: endpoint.method as any,
      ...rest,
    })
      .then((data) => JSON.stringify(data, null, 2))
      .then(onResponseCallback);
  };

  return (
    <>
      <Button
        aria-label={t('APIHelper.executeRequest') as string}
        variant="ghost"
        size="small"
        icon={IconPlay}
        onClick={onRequest}
        loading={isLoading}
        disabled={!isValid}
        round
      />
      {children}
      <Button
        aria-label={t('copyToClipboard') as string}
        variant="ghost"
        size="small"
        icon={copiedToClipboard ? IconCheck2 : IconCopy}
        onClick={onCopyToClipboard(content || '')}
        round
      />
    </>
  );
};

interface ExampleRequestProps extends Omit<ToolbarProps, 'path' | 'content' | 'apiKey'> {
  mode?: 'sh' | 'py' | 'js';
  server: string;
  queryParameters: ApiHelperContextType['queryParameters'];
  pathParameters: ApiHelperContextType['pathParameters'];
}

const ExampleRequest: FC<ExampleRequestProps> = ({
  mode = 'sh',
  isValid = true,
  server,
  endpoint,
  pathParameters,
  queryParameters,
  data,
  onResponseCallback,
}) => {
  const { t } = useTranslation();

  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const onShowApiKeyToggle = useCallback(() => setShowApiKey((previous) => !previous), []);

  const path = getEndpointPath(server, endpoint.path, pathParameters, queryParameters);
  const apiKey = pathParameters.api_key as string;

  const isValidEndpointPath = useMemo(() => getIsValidEndpointPath(path), [path]);
  const [exampleCommandWithHiddenKey, exampleCommand] = useMemo(
    () =>
      getRequestOutputs(
        {
          path,
          endpoint,
          apiKey,
          payload: endpoint.requestBody ? data : undefined,
        },
        mode,
      ),
    [mode, path, endpoint, apiKey, data],
  );

  return (
    <CodeEditor
      mode={mode}
      value={showApiKey ? exampleCommand : exampleCommandWithHiddenKey}
      toolbar={
        <Toolbar
          path={path}
          endpoint={endpoint}
          apiKey={apiKey}
          content={exampleCommand}
          onResponseCallback={onResponseCallback}
          data={data}
          isValid={isValid && !!apiKey && isValidEndpointPath}
        >
          {exampleCommand !== exampleCommandWithHiddenKey && (
            <Button
              aria-label={t('APIHelper.showApiKey') as string}
              variant="ghost"
              size="small"
              icon={showApiKey ? IconEyeOff : IconEye}
              onClick={onShowApiKeyToggle}
              round
            />
          )}
        </Toolbar>
      }
      lineNumbers={false}
      lineWrapping
      multiLine
      readOnly
    />
  );
};

const ExampleRequests: FC = () => {
  const { t } = useTranslation();

  const [requestResponse, setRequestResponse] = useState<string | null>(null);
  const { server, endpoint, pathParameters, queryParameters, payload } =
    useContext(ApiHelperContext);

  const isValidPayload = useMemo(
    () => (endpoint.requestBody && payload ? getIsValidPayload(payload) : true),
    [endpoint.requestBody, payload],
  );

  return (
    <>
      <Box my="$32">
        <Tabs>
          <Tabs.Tab>curl</Tabs.Tab>
          <Tabs.Tab>Python</Tabs.Tab>
          <Tabs.Tab>JavaScript</Tabs.Tab>

          <Tabs.Panel>
            <ExampleRequest
              mode="sh"
              server={server}
              endpoint={endpoint}
              pathParameters={pathParameters}
              queryParameters={queryParameters}
              data={payload}
              onResponseCallback={setRequestResponse}
              isValid={isValidPayload}
            />
          </Tabs.Panel>

          <Tabs.Panel>
            <ExampleRequest
              mode="py"
              server={server}
              endpoint={endpoint}
              pathParameters={pathParameters}
              queryParameters={queryParameters}
              data={payload}
              onResponseCallback={setRequestResponse}
              isValid={isValidPayload}
            />
          </Tabs.Panel>

          <Tabs.Panel>
            <ExampleRequest
              mode="js"
              server={server}
              endpoint={endpoint}
              pathParameters={pathParameters}
              queryParameters={queryParameters}
              data={payload}
              onResponseCallback={setRequestResponse}
              isValid={isValidPayload}
            />
          </Tabs.Panel>
        </Tabs>
      </Box>

      {!!requestResponse && (
        <Box display="flex" flexDirection="column" mb="$32">
          <Typography mt="$24" mb="$8">
            {t('APIHelper.requestResponse')}
          </Typography>
          <CodeEditor
            mode="json"
            value={`${requestResponse}`}
            lineNumbers={false}
            maxHeight={500}
            multiLine
            copyToClipboard
            lineWrapping
            readOnly
          />
        </Box>
      )}
    </>
  );
};

const ParameterComponent: FC<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: FC<any>;
  [key: string]: unknown;
}> = ({ component: Component, ...props }) => (Component ? <Component {...props} /> : null);

const ApiHelperEndpoint: FC<{
  components: OpenApi.ComponentsObject['schemas'];
  isIntegrationEndpoint: boolean;
}> = ({ isIntegrationEndpoint, ...props }) => {
  const { t } = useTranslation();

  const {
    endpoint,
    pathParameters,
    queryParameters,
    setPathParameter,
    setPathParameters,
    setQueryParameter,
    setQueryParameters,
  } = useContext(ApiHelperContext);

  const endpointLink = useMemo(() => getEndpointLink(endpoint), [endpoint]);
  const { endpointPathParameterEntries, endpointQueryParameterEntries } = useMemo(() => {
    const parametersByType = getEndpointParameters(endpoint.parameters);
    const queryParameterEntries = Object.entries(parametersByType.query);
    return {
      endpointPathParameterEntries: Object.entries(parametersByType.path),
      endpointQueryParameterEntries: isIntegrationEndpoint
        ? queryParameterEntries.filter(([parameterName]) => parameterName !== 'token')
        : queryParameterEntries,
    };
  }, [endpoint.parameters, isIntegrationEndpoint]);

  const queryPathParameters = useMemo(
    () => ({ ...pathParameters, ...queryParameters }),
    [pathParameters, queryParameters],
  );

  return (
    <>
      {endpoint.permissions && <SelectApiKey />}

      {endpointPathParameterEntries.length > 0 && (
        <Box pt="$8" pb="$16">
          <Divider label={t('APIHelper.pathParameters')} />
        </Box>
      )}

      {endpointPathParameterEntries.map(([parameterName, parameterProps]) => (
        <ParameterComponent
          key={`path-${parameterName}`}
          component={parameterMapper[parameterName as keyof typeof parameterMapper]}
          pathParameters={pathParameters}
          value={pathParameters[parameterName] ?? ''}
          setValue={setPathParameter}
          setValues={setPathParameters}
          {...parameterProps}
        />
      ))}

      {endpointQueryParameterEntries.length > 0 && (
        <Box pt="$8" pb="$16">
          <Divider label={t('APIHelper.queryParameters')} />
        </Box>
      )}

      {endpointQueryParameterEntries.map(([parameterName, parameterProps]) =>
        parameterMapper[parameterName as keyof typeof parameterMapper] ? (
          <ParameterComponent
            key={`query-${parameterName}`}
            component={parameterMapper[parameterName as keyof typeof parameterMapper]}
            pathParameters={queryPathParameters}
            value={queryParameters[parameterName] ?? ''}
            setValue={setQueryParameter}
            setValues={setQueryParameters}
            {...parameterProps}
          />
        ) : (
          <ApiHelperParameter
            key={`query-${parameterName}`}
            parameter={parameterProps}
            value={queryParameters[parameterProps.name ?? parameterName]}
            onParameterChange={setQueryParameter}
            {...props}
          />
        ),
      )}

      <ExamplePayload key={endpoint.path} />

      <Divider />

      <Box
        display="flex"
        alignItems="center"
        mb={endpoint.description ? '$16' : '$24'}
        mt="$24"
        gap={8}
      >
        {endpoint && <Badge {...getEndpointBadgeOption(endpoint.method)} size="small" />}
        {endpointLink && (
          <Link href={endpointLink} iconAfter={IconShare} target="_blank">
            <Typography as="h3">{endpoint.summary}</Typography>
          </Link>
        )}
      </Box>

      {!!endpoint.description && (
        <Box mb="$24">
          <Typography lineHeight={1.4} truncate={3}>
            {endpoint.description}
          </Typography>
        </Box>
      )}

      {endpointPathParameterEntries.map(([parameterName, parameter]) => (
        <ApiHelperInput
          key={parameterName}
          label={parameter.description ?? 'unknown'}
          value={pathParameters[parameterName] ?? ''}
        />
      ))}

      <Divider />

      <ExampleRequests />
    </>
  );
};

const ApiHelperContextContent: FC<{ externalApi: ExternalApi; endpoint: ExternalApiEndpoint }> = ({
  externalApi,
  endpoint,
}) => {
  const [server] = useState(() => getApiServer());
  const [pathParameters, setPathParameters] = usePathParameters();
  const [queryParameters, setQueryParameters] = useState<ApiHelperContextType['queryParameters']>(
    {},
  );
  const [payloads, setPayloads] = useState<ApiHelperContextType['payloads']>({
    list: [],
    required: false,
  });
  const [payload, setPayload] = useState<ApiHelperContextType['payload']>(undefined);

  const apiKey = pathParameters.api_key;
  const isIntegrationEndpoint = getIsIntegrationEndpoint(endpoint);

  const derivedQueryParameters = useMemo(() => {
    if (isIntegrationEndpoint) {
      queryParameters.token = apiKey;
    }
    return queryParameters;
  }, [isIntegrationEndpoint, apiKey, queryParameters]);

  useEffect(() => {
    setQueryParameters({});
  }, [endpoint]);

  useEffect(() => {
    if (endpoint && externalApi.components?.schemas) {
      const payloadInputs = getPayloadInputs(endpoint, externalApi.components.schemas);
      setPayloads(payloadInputs);
      setPayload(payloadInputs.required ? payloadInputs.list[0]?.payload : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const setPathParameter = useCallback(
    (
      key: keyof ApiHelperContextType['pathParameters'],
      value: ApiHelperContextType['pathParameters'][keyof ApiHelperContextType['pathParameters']],
    ) =>
      setPathParameters((params) => ({
        ...params,
        [key]: value,
      })),
    [setPathParameters],
  );

  const setQueryParameter = useCallback(
    (
      key: keyof ApiHelperContextType['queryParameters'],
      value: ApiHelperContextType['queryParameters'][keyof ApiHelperContextType['queryParameters']],
    ) =>
      setQueryParameters((params) => ({
        ...params,
        [key]: value !== '' ? value : undefined,
      })),
    [],
  );

  const contextValue = useMemo(
    () => ({
      server,
      endpoint,
      pathParameters,
      setPathParameters,
      setPathParameter,
      queryParameters: derivedQueryParameters,
      setQueryParameters,
      setQueryParameter,
      payload,
      setPayload,
      payloads,
    }),
    [
      server,
      endpoint,
      pathParameters,
      setPathParameters,
      setPathParameter,
      derivedQueryParameters,
      setQueryParameter,
      payload,
      payloads,
    ],
  );

  return (
    <ApiHelperContext.Provider value={contextValue as ApiHelperContextType}>
      <ApiHelperEndpoint
        components={externalApi.components?.schemas}
        isIntegrationEndpoint={isIntegrationEndpoint}
      />
    </ApiHelperContext.Provider>
  );
};

const ApiHelperContent: FC<{ externalApi: ExternalApi }> = ({ externalApi }) => {
  const { t } = useTranslation();
  const [endpoint, setEndpoint] = useState<ExternalApiEndpoint | undefined>(undefined);
  const onSelectEndpoint = (target: string) => setEndpoint(externalApi.endpoints[target]);

  /**
   * Maps predefined API options to be used in a `Select` component
   */
  const endpointsItems = useMemo(
    () => getEndpointsOptions(externalApi.endpoints),
    [externalApi.endpoints],
  );

  return (
    <>
      <Box mt="$32" mb="$16">
        <Select
          label={t('selectApiOptionPrompt')}
          items={endpointsItems}
          onChange={onSelectEndpoint}
          value={endpoint?.operationId ?? ''}
          placeholder={`${t('choose')}...`}
          searchable
        />
      </Box>

      {endpoint ? (
        <ApiHelperContextContent externalApi={externalApi} endpoint={endpoint} />
      ) : (
        <Typography as="h3" my="$24">
          {t('methodNotChosen')}
        </Typography>
      )}
    </>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
}

const NewApiHelper: FC<Props> = ({ open, onClose, children }) => {
  const { t } = useTranslation();
  const { data: externalApi, isLoading: externalApiIsLoading } = useExternalApiQuery({
    cacheTime: Infinity,
    enabled: open,
  });

  return (
    <Drawer open={open && !externalApiIsLoading} onClose={onClose} width={580}>
      <Drawer.Header>
        <Drawer.Header.Title title={t('APIHelper.title')} />
        <Drawer.Header.Description>
          <Typography color="grey20" mb="$8" mt="$12">
            {t('APIHelper.description')}
          </Typography>
          <Button
            forwardedAs="a"
            href={DocsLinks.API}
            icon={IconFileText}
            variant="secondary"
            target="_blank"
          >
            {t('seeRobocorpDocs')}
          </Button>
        </Drawer.Header.Description>
      </Drawer.Header>
      {externalApi && (
        <Drawer.Content>
          <ErrorBoundary>
            <ApiHelperContent externalApi={externalApi} />
          </ErrorBoundary>
        </Drawer.Content>
      )}
      {children}
    </Drawer>
  );
};

const API_HELPER_KEY = 'V1apiHelper';

export const ApiHelper: FC<Props> = withErrorBoundary(({ open, onClose, ...restProps }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const onCloseApiHelperSearch = useCallback(
    () =>
      setSearchParams((previousSearchParams) => {
        previousSearchParams.delete(API_HELPER_KEY);
        return previousSearchParams;
      }),
    [setSearchParams],
  );

  if (searchParams.get(API_HELPER_KEY) === 'true') {
    return <OldApiHelper open onClose={onCloseApiHelperSearch} {...restProps} />;
  }

  return <NewApiHelper open={open} onClose={onClose} {...restProps} />;
});
